import { useTranslation } from "react-i18next";
import { ArrowRight2, Buildings, DocumentText1, MoneySend, Profile2User } from "iconsax-react";
import type { Icon } from "iconsax-react";
import { Button } from "@/shared/ui";
import { formatMoneyKzt, type Locale } from "@/shared/utils";

interface CardAction {
  label: string;
  icon: Icon;
  onClick: () => void;
}

interface Props {
  stats: {
    totalEmployees: number;
    totalSalary: number;
  };
  showStructureActions?: boolean;
  onDepartmentsClick?: () => void;
  onStaffingTableClick?: () => void;
}

export default function EmployeesSummaryCards({
  stats,
  showStructureActions,
  onDepartmentsClick,
  onStaffingTableClick,
}: Props) {
  const { t, i18n } = useTranslation("EmployeesPage");
  const locale = (i18n.language as Locale) || "ru";

  const cards: Array<{ key: string; label: string; value: string; icon: Icon; action?: CardAction }> = [
    {
      key: "total",
      label: t("summary.totalEmployees"),
      value: String(stats.totalEmployees),
      icon: Profile2User,
      action:
        showStructureActions && onDepartmentsClick
          ? { label: t("departments.configureAction"), icon: Buildings, onClick: onDepartmentsClick }
          : undefined,
    },
    {
      key: "fund",
      label: t("summary.fund"),
      value: formatMoneyKzt(stats.totalSalary, locale),
      icon: MoneySend,
      action:
        showStructureActions && onStaffingTableClick
          ? { label: t("staffingTable.title"), icon: DocumentText1, onClick: onStaffingTableClick }
          : undefined,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-2 mb-4">
      {cards.map(function renderCard(card) {
        const Icon = card.icon;
        const ActionIcon = card.action?.icon;
        return (
          <div key={card.key} className="surface-container-fill radius-3xl p-5 flex flex-col gap-3">
            <div className="flex justify-start items-center gap-2">
              <div className="w-8 h-8 radius-xs surface-component-fill flex items-center justify-center content-action-neutral shrink-0">
                <Icon size={16} color="currentColor" />
              </div>
              <p className="text-label-md content-base-primary">{card.label}</p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-display-sm content-base-primary">{card.value}</p>
            </div>
            {card.action && ActionIcon && (
              <Button variant="tertiary" size="lg" className="w-full mt-auto" onClick={card.action.onClick}>
                <span className="flex flex-1 min-w-0 items-center gap-2 text-left">
                  <span className="shrink-0 content-base-secondary">
                    <ActionIcon size={16} color="currentColor" />
                  </span>
                  <span className="truncate">{card.action.label}</span>
                </span>
                <span className="shrink-0 content-base-secondary">
                  <ArrowRight2 size={16} color="currentColor" />
                </span>
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
